'use client';

import React from 'react';
import {
  Text,
  Heading
} from '../ui/design-system';
import { cn } from '@/lib/utils';
import { Mail, Phone, MapPin } from 'lucide-react';

interface LegalContactInfoProps {
  className?: string;
  title: string;
  introText?: string; // Made optional as not all contexts might have it
  companyName: string;
  addressLines: string[];
  phone: string;
  phoneHref: string;
  email: string;
  emailHref: string;
}

/**
 * LegalContactInfo Component
 * 
 * Displays standardized contact information for legal pages
 * following the design system patterns.
 */
export function LegalContactInfo({
  className,
  title,
  introText,
  companyName,
  addressLines,
  phone,
  phoneHref,
  email,
  emailHref,
}: LegalContactInfoProps) {
      return (
    <div className={cn('', className)}>
      <Heading 
        level={2} 
        className="text-2xl font-semibold mb-3 text-gray-800 dark:text-gray-100"
      >
        {title}
      </Heading>
      {introText && (
        <Text className="mt-4 mb-5 text-base text-gray-700 dark:text-gray-300">
          {introText}
        </Text>
      )}
      <div className="mt-6 space-y-3">
        {/* Company & Address Block */}
        <div className="flex items-start">
          <MapPin className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-3 mt-1 flex-shrink-0" />
          <div>
            <Text className="font-semibold text-gray-800 dark:text-gray-100">{companyName}</Text>
            {addressLines.map((line, index) => (
              <Text key={index} className="text-sm text-gray-600 dark:text-gray-400">{line}</Text>
            ))}
          </div>
        </div>
        
        {/* Phone Block */}
        <div className="flex items-center">
          <Phone className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-3 flex-shrink-0" />
          <Text className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-semibold text-gray-800 dark:text-gray-100">Phone:</span>{' '}
            <a 
              href={phoneHref} 
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              {phone}
            </a>
          </Text>
        </div>
        
        {/* Email Block */}
        <div className="flex items-center">
          <Mail className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-3 flex-shrink-0" />
          <Text className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-semibold text-gray-800 dark:text-gray-100">Email:</span>{' '}
            <a 
              href={emailHref} 
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              {email}
            </a>
          </Text>
        </div>
      </div>
    </div>
  );
}
